import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkle, X, CaretDown, ClockCounterClockwise, ArrowUp, ArrowDown, CheckCircle, Lightbulb } from "@phosphor-icons/react";

const money0 = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

export function MonthlyReviewCard() {
  const [review, setReview] = useState(null);
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    api.get("/monthly-review")
      .then((r) => { if (r.data.available && !r.data.review.dismissed) setReview(r.data.review); })
      .catch(() => {});
  }, []);

  const dismiss = async () => {
    const id = review.id;
    setReview(null);
    try { await api.post(`/monthly-review/${id}/dismiss`); } catch { /* already gone from UI */ }
  };

  const toggleHistory = async () => {
    if (!showHistory && history === null) {
      try {
        const { data } = await api.get("/monthly-review/history");
        setHistory((data.reviews || []).filter((r) => r.month !== review.month));
      } catch {
        setHistory([]);
      }
    }
    setShowHistory((s) => !s);
  };

  if (!review) return null;
  const t = review.totals || {};
  const netUp = (t.net_change ?? 0) >= 0;

  return (
    <div className="bk-card p-6 mb-10" style={{ borderColor: "rgba(232,168,56,0.4)", background: "linear-gradient(180deg, var(--bk-rowan-bg), #fff 55%)" }} data-testid="monthly-review-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "rgba(232,168,56,0.18)" }}>
            <Sparkle size={22} weight="duotone" color="var(--bk-accent)" />
          </span>
          <div>
            <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-accent)" }}>Rowan&apos;s Monthly Review</p>
            <h3 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }} data-testid="monthly-review-month">{review.month_label}</h3>
          </div>
        </div>
        <button onClick={dismiss} data-testid="monthly-review-dismiss" className="p-2 rounded-full hover:bg-[#F5F5F0] transition-colors" title="Dismiss until next month">
          <X size={18} weight="bold" color="var(--bk-text-2)" />
        </button>
      </div>

      <p className="text-sm leading-relaxed mt-4" style={{ color: "var(--bk-text)" }} data-testid="monthly-review-note">{review.note}</p>

      <div className="flex flex-wrap gap-3 mt-4">
        <div className="rounded-2xl border px-4 py-2.5" style={{ borderColor: "var(--bk-border)", background: "#fff" }} data-testid="monthly-review-net">
          <p className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Net for the month</p>
          <p className="font-display text-xl font-bold inline-flex items-center gap-2" style={{ color: t.net >= 0 ? "var(--bk-success)" : "#B23B1F" }}>
            {money0(t.net)}
            {t.net_change !== null && t.net_change !== undefined && (
              <span className="text-xs font-semibold inline-flex items-center gap-0.5" style={{ color: netUp ? "var(--bk-success)" : "#B23B1F" }}>
                {netUp ? <ArrowUp size={11} weight="bold" /> : <ArrowDown size={11} weight="bold" />}{money0(Math.abs(t.net_change))}
              </span>
            )}
          </p>
        </div>
        {(review.businesses || []).slice(0, 3).map((b) => (
          <div key={b.business_id} className="rounded-2xl border px-4 py-2.5" style={{ borderColor: "var(--bk-border)", background: "#fff" }}>
            <p className="text-[10px] uppercase tracking-[0.14em] font-semibold truncate max-w-[160px]" style={{ color: "var(--bk-text-2)" }}>{b.name}</p>
            <p className="font-display text-xl font-bold" style={{ color: b.net >= 0 ? "var(--bk-text)" : "#B23B1F" }}>{money0(b.net)}</p>
          </div>
        ))}
      </div>

      {(review.highlights || []).length > 0 && (
        <ul className="mt-4 space-y-1.5" data-testid="monthly-review-highlights">
          {review.highlights.map((h, i) => (
            <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "var(--bk-text)" }}>
              <CheckCircle size={16} weight="duotone" color="var(--bk-success)" className="shrink-0 mt-0.5" /> {h}
            </li>
          ))}
        </ul>
      )}

      {(review.suggestions || []).length > 0 && (
        <div className="mt-4 rounded-xl p-3.5 space-y-1.5" style={{ background: "#F6EEDF" }} data-testid="monthly-review-suggestions">
          {review.suggestions.map((s, i) => (
            <p key={i} className="flex items-start gap-2 text-sm" style={{ color: "var(--bk-text)" }}>
              <Lightbulb size={16} weight="fill" color="#8A6A3B" className="shrink-0 mt-0.5" /> {s}
            </p>
          ))}
        </div>
      )}

      <button onClick={toggleHistory} data-testid="monthly-review-history-toggle" className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold hover:underline" style={{ color: "var(--bk-text-2)" }}>
        <ClockCounterClockwise size={14} weight="bold" /> Past reviews
        <CaretDown size={12} weight="bold" style={{ transform: showHistory ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }} />
      </button>

      {showHistory && (
        <div className="mt-3 space-y-2" data-testid="monthly-review-history">
          {history === null ? (
            <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>Loading…</p>
          ) : history.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>This is your first monthly review — history builds from here.</p>
          ) : (
            history.map((r) => (
              <div key={r.id} className="rounded-xl border p-3.5" style={{ borderColor: "var(--bk-border)", background: "#fff" }}>
                <p className="text-xs font-semibold" style={{ color: "var(--bk-accent)" }}>{r.month_label}</p>
                <p className="text-sm mt-1" style={{ color: "var(--bk-text)" }}>{r.note}</p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
